/**
 * Admin → Extractors.
 *
 * Backend: `GET /admin/extractors` (app/routers/admin_extractors.py) — a
 * READ-ONLY view of the LIVE extraction routing config. It is computed from
 * EXTRACTION_ROUTING / LLM_TARGET_BY_DOCTYPE / ANALYZER_REGISTRY at request
 * time, so a routing change in the backend shows up here with no frontend
 * deploy. There is nothing to edit — this screen answers "what actually runs for
 * this docType, and how has it been doing for 30 days?".
 *
 * Each row carries `pipelineSteps` in execution order (a wired skill extractor
 * runs first, then the configured routing methods), plus `stats30d`
 * {total, succeeded, rejected, successRate, rejectionRate, methodMix}.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import { AdminGrid } from "../shared/adminGrid";
import SearchInput from "../../../components/inputs/SearchInput";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminExtractorsApi } from "../../../api/adminPlatformApi";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { AdminCard, AdminEmpty, AdminScreenShell } from "../shared/AdminScreenShell";

const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);

export default function AdminExtractorsScreen() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminExtractorsApi
      .list()
      .then(setData)
      .catch((err) => {
        setData(null);
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't load the extractor registry",
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.rows || [];

  const columnDefs = useMemo(
    () => [
      { headerName: "Doc type", field: "docType", minWidth: 260, flex: 1.4, sort: "asc" },
      {
        headerName: "Pipeline",
        colId: "pipeline",
        minWidth: 300,
        flex: 1.6,
        valueGetter: (p) =>
          (p.data?.pipelineSteps || []).map((s) => s.kind).join(" → "),
        cellRenderer: (p) => (
          <Flx ac g={0.5} wrap>
            {(p.data?.pipelineSteps || []).length ? (
              p.data.pipelineSteps.map((s) => (
                <Chip
                  key={s.step}
                  size="small"
                  label={s.kind}
                  sx={{
                    height: 19,
                    fontSize: 10.5,
                    fontWeight: 600,
                    bgcolor:
                      s.kind === "skill_two_tool" ? "#dcfce7" : "#f1f5f9",
                    color: s.kind === "skill_two_tool" ? "#166534" : "#475569",
                  }}
                />
              ))
            ) : (
              <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>no routing</Txt>
            )}
          </Flx>
        ),
      },
      {
        headerName: "Skill",
        colId: "skill",
        width: 190,
        valueGetter: (p) => p.data?.skill?.skillName || "",
        cellRenderer: (p) => {
          const s = p.data?.skill;
          if (!s) return <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>—</Txt>;
          return (
            <Flx ac g={0.75}>
              <Txt sx={{ fontSize: 12, color: "#0f172a" }}>{s.skillName}</Txt>
              {!p.data.skillWired ? (
                <Chip
                  size="small"
                  label="pending"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: "#fef3c7",
                    color: "#92400e",
                  }}
                />
              ) : null}
            </Flx>
          );
        },
      },
      {
        headerName: "CU analyzer",
        field: "cuAnalyzer",
        minWidth: 180,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "LLM target",
        field: "llmTarget",
        minWidth: 180,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Runs 30d",
        colId: "total",
        width: 110,
        valueGetter: (p) => p.data?.stats30d?.total ?? 0,
      },
      {
        headerName: "Success",
        colId: "successRate",
        width: 110,
        valueGetter: (p) => p.data?.stats30d?.successRate ?? null,
        valueFormatter: (p) => pct(p.value),
        cellStyle: (p) =>
          p.value != null && p.value < 0.8 ? { color: "#b45309" } : undefined,
      },
      {
        headerName: "Rejected",
        colId: "rejectionRate",
        width: 110,
        valueGetter: (p) => p.data?.stats30d?.rejectionRate ?? null,
        valueFormatter: (p) => pct(p.value),
        cellStyle: (p) =>
          p.value != null && p.value > 0.2 ? { color: "#b91c1c" } : undefined,
      },
    ],
    [],
  );

  return (
    <AdminScreenShell
      icon={<AccountTreeOutlined />}
      title="Extractors"
      count={rows.length || undefined}
      loading={loading}
      subtitle="Read-only view of the live extraction routing: which method runs first for each document type, the analyzer and LLM target behind it, and how it has performed over the last 30 days. Routing is code-owned — changes here require a backend change, not a config edit."
      actions={
        <>
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder="Search doc types…"
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
        </>
      }
    >
      {data?.totals ? (
        <Flx g={1} sx={{ mb: 2 }} wrap>
          <Chip
            size="small"
            label={`${data.totals.docTypesConfigured} doc types`}
            sx={{ fontSize: 11, fontWeight: 600, height: 22 }}
          />
          <Chip
            size="small"
            label={`${data.totals.docTypesWithSkill} with a wired skill`}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              height: 22,
              bgcolor: "#dcfce7",
              color: "#166534",
            }}
          />
          <Chip
            size="small"
            label={`${data.totals.docTypesWithCU} with a CU analyzer`}
            sx={{ fontSize: 11, fontWeight: 600, height: 22 }}
          />
          <Chip
            size="small"
            label={`${data.totals.docTypesWithLLM} with an LLM target`}
            sx={{ fontSize: 11, fontWeight: 600, height: 22 }}
          />
          <Txt sx={{ fontSize: 11.5, color: "#94a3b8", alignSelf: "center" }}>
            generated {formatDateTime(data.generatedAt) || "—"} · {data.windowDays}
            d window
          </Txt>
        </Flx>
      ) : null}

      <AdminCard dense>
        {!rows.length ? (
          <AdminEmpty>
            {loading ? "Loading…" : "No extractor rows returned."}
          </AdminEmpty>
        ) : (
          <AdminGrid rowCount={(rows || []).length} maxRows={12}>
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                resizable: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
              }}
              getRowId={(p) => String(p.data.docType)}
              quickFilterText={quickFilter}
              loading={loading}
              onRowClicked={(e) => setSelected(e.data)}
            />
          </AdminGrid>
        )}
      </AdminCard>

      {selected ? (
        <AdminCard
          title={`Pipeline — ${selected.docType}`}
          endContent={
            <Txt
              sx={{ fontSize: 12, color: "#64748b", cursor: "pointer" }}
              onClick={() => setSelected(null)}
            >
              Close
            </Txt>
          }
        >
          <Flx column g={1.5}>
            {(selected.pipelineSteps || []).map((s) => (
              <Box
                key={s.step}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  p: 1.5,
                  bgcolor: "#fff",
                }}
              >
                <Flx ac g={1} wrap sx={{ mb: 0.5 }}>
                  <Chip
                    size="small"
                    label={`step ${s.step}`}
                    sx={{ height: 19, fontSize: 10.5, fontWeight: 700 }}
                  />
                  <Txt sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                    {s.kind}
                  </Txt>
                  {s.name ? (
                    <Txt sx={{ fontSize: 12, color: "#475569" }}>{s.name}</Txt>
                  ) : null}
                  {s.model ? (
                    <Chip
                      size="small"
                      label={s.model}
                      sx={{ height: 19, fontSize: 10.5 }}
                    />
                  ) : null}
                </Flx>
                <Txt sx={{ fontSize: 12, color: "#64748b", display: "block" }}>
                  {s.description}
                </Txt>
                {s.analyzer ? (
                  <Txt sx={{ fontSize: 11.5, color: "#475569", display: "block", mt: 0.5 }}>
                    analyzer: {s.analyzer}
                  </Txt>
                ) : null}
                {s.llmTarget ? (
                  <Txt sx={{ fontSize: 11.5, color: "#475569", display: "block", mt: 0.5 }}>
                    target: {s.llmTarget}
                    {s.scoredFields?.length
                      ? ` · scored: ${s.scoredFields.join(", ")}`
                      : ""}
                  </Txt>
                ) : null}
                {s.schemas?.length ? (
                  <Txt sx={{ fontSize: 11.5, color: "#475569", display: "block", mt: 0.5 }}>
                    schemas: {s.schemas.join(", ")}
                  </Txt>
                ) : null}
              </Box>
            ))}

            {Object.keys(selected.stats30d?.methodMix || {}).length ? (
              <Box>
                <Txt
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#0f172a",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Method mix (30d)
                </Txt>
                <Flx g={0.75} wrap>
                  {Object.entries(selected.stats30d.methodMix).map(([m, n]) => (
                    <Chip
                      key={m}
                      size="small"
                      label={`${m}: ${n}`}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  ))}
                </Flx>
              </Box>
            ) : null}
          </Flx>
        </AdminCard>
      ) : null}
    </AdminScreenShell>
  );
}
